import { formatDatumIdo, telefonMegjelenites } from '../lib/formaz.ts';
import { STATUSZ_CIMKE, type LeadStatusz } from '../lib/tipusok.ts';

const SZAMSZAVAK = ['', 'Egy', 'Két', 'Három', 'Négy', 'Öt', 'Hat', 'Hét', 'Nyolc', 'Kilenc', 'Tíz'];

/** 1 → „Egy”, 2 → „Két” (jelzőként), 4 → „Négy”; tíz fölött számjegy. */
export function szamSzo(n: number): string {
  return n >= 1 && n <= 10 ? SZAMSZAVAK[n]! : String(n);
}

/** Mióta vár valaki: „12 perce”, „2 órája”, „1 napja” (a „már … vár” mondatba). */
export function varakozasSzoveg(perc: number): string {
  if (perc < 1) return 'most';
  if (perc < 60) return `${Math.floor(perc)} perce`;
  if (perc < 1440) return `${Math.floor(perc / 60)} órája`;
  return `${Math.floor(perc / 1440)} napja`;
}

const IDOTOLDAS_KUSZOB_PERC = 15;

/** Az oldalfej mondata: hány új érdeklődő vár hívásra, és a legrégebbi mióta. */
export function fejMondat({ ujDb, legregebbiPerc }: { ujDb: number; legregebbiPerc: number | null }): string {
  if (ujDb === 0) return 'Nincs hívásra váró új érdeklődő.';
  const alap = `${szamSzo(ujDb)} érdeklődő vár hívásra`;
  if (legregebbiPerc !== null && legregebbiPerc >= IDOTOLDAS_KUSZOB_PERC) return `${alap}, a legrégebbi már ${varakozasSzoveg(legregebbiPerc)}.`;
  return `${alap}.`;
}

/** „Csütörtök, október 8.” budapesti időben. */
export function napSzoveg(nowMs: number): string {
  const r: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('hu-HU', { timeZone: 'Europe/Budapest', weekday: 'long', month: 'long', day: 'numeric' }).formatToParts(new Date(nowMs))) r[p.type] = p.value;
  const nap = r.weekday!;
  return `${nap[0]!.toUpperCase()}${nap.slice(1)}, ${r.month} ${r.day}.`;
}

export interface CsvSor {
  created_at: string; nev: string; telefon: string; statusz: LeadStatusz; tipus: string; terulet: string | null; cim: string;
  becsles_min: number | null; becsles_max: number | null; szakertoi: boolean; pontszam: number; felelos: string | null; iroda: string | null;
}

/** +36 30 412 8890 helyett 06 30 412 8890: a „+” kezdetű cellát az Excel képletnek venné. */
function telefonCsvhez(e164: string): string {
  const szep = telefonMegjelenites(e164);
  return szep.startsWith('+36 ') ? `06 ${szep.slice(4)}` : szep;
}

/** Egy cella: képletindító karakter elé aposztróf (Excel-képletinjektálás ellen), majd idézés, ha kell. */
function cella(ertek: string | number | null): string {
  if (ertek === null) return '';
  let t = String(ertek);
  if (typeof ertek === 'string' && /^[=+\-@\t\r]/.test(t)) t = `'${t}`;
  return /[;"\r\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
}

/** Excelbarát CSV: UTF-8 BOM, pontosvessző, CRLF. Az iroda oszlop csak több iroda együttes listájánál van. */
export function csvTartalom(sorok: readonly CsvSor[], irodaOszloppal: boolean): string {
  const fejlec = ['Beérkezett', 'Név', 'Telefon', 'Állapot', 'Típus', 'Terület', 'Cím', 'Becslés alsó (Ft)', 'Becslés felső (Ft)', 'Pontszám', 'Felelős'];
  if (irodaOszloppal) fejlec.push('Iroda');
  const adat = sorok.map((s) => {
    const ar = (v: number | null) => (s.szakertoi || v === null ? 'Szakértői becslés' : v);
    const cellak = [
      formatDatumIdo(s.created_at), s.nev, telefonCsvhez(s.telefon), STATUSZ_CIMKE[s.statusz], s.tipus, s.terulet, s.cim,
      ar(s.becsles_min), ar(s.becsles_max), s.pontszam, s.felelos,
    ];
    if (irodaOszloppal) cellak.push(s.iroda);
    return cellak.map(cella).join(';');
  });
  return `﻿${[fejlec.join(';'), ...adat].join('\r\n')}`;
}
